import React from 'react';
import * as styles from './Table.css';
import * as viewMoreStyles from './ViewMoreTable.css';
import { Progress } from '@instamenu/ui-system-next';
import { useLocale } from '@instamenu/context-i18n';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  flexRender,
  SortingState,
  functionalUpdate,
  RowData,
  TableOptions,
} from '@tanstack/react-table';
import { clsx } from 'clsx';

type TableProps<TData extends RowData> = {
  data?: TData[];
  onRowClick?: (row: TData) => void;
  onSortingChange?: (sorting: SortingState) => void;
  loading?: boolean;
  fetchMore?: () => void;
  hasMore?: boolean;
  sorting?: SortingState;
} & Pick<TableOptions<TData>, 'columns'>;

const empty: never[] = [];

export function ConnectionTable<T extends object>({
  data,
  columns,
  onRowClick,
  onSortingChange = () => undefined,
  loading = false,
  fetchMore,
  hasMore,
  sorting = [],
}: TableProps<T>) {
  const { i18n } = useLocale();

  const table = useReactTable({
    data: data ?? empty,
    columns,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    state: {
      sorting,
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    onSortingChange: (input) => onSortingChange(functionalUpdate(input, sorting)),
  });

  const handleViewMore = () => {
    if (fetchMore) fetchMore();
  };

  return (
    <div style={{ position: 'relative' }}>
      <div className={styles.scrollWrap}>
        <table className={styles.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className={header.column.columnDef.meta?.style}
                    style={{
                      width: header.column.columnDef.size,
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <span
                        className={clsx([
                          styles.arrows,
                          header.column.getIsSorted() === 'asc' ? styles.ascending : false,
                          header.column.getIsSorted() === 'desc' ? styles.descending : false,
                        ])}
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={onRowClick ? styles.clickableRow : undefined}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <td className={cell.column.columnDef.meta?.style} key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        {hasMore && (
          <div className={viewMoreStyles.viewMore} onClick={handleViewMore}>
            {i18n.t('general.table.viewMore')}
          </div>
        )}
      </div>
      {loading && (
        <div className={styles['loading']}>
          <Progress size={32} />
        </div>
      )}
    </div>
  );
}
